import { flatten } from 'lodash';

/* 
  References: 
    https://developer.mozilla.org/en-US/docs/Web/Media/Formats/Image_types#common_image_file_types
    https://developer.mozilla.org/en-US/docs/Web/Media/Formats/Containers
*/
export const FILE_EXTENSIONS = [
  'apng',
  // 'avif',
  'gif',
  'jpg',
  'jpeg',
  'jfif',
  'pjpeg',
  'pjp',
  'png',
  'webp',

  'mp3',
  'wav',
  '3gp',
  'mpg',
  'mpeg',
  'm4a',
  'oga',
  'ogg',
  'webm',

  'mp4',
  'webm',
  '3gp',
  'mpg',
  'mpeg',
  'm4v',
  'm4p',
  'ogv',
  'ogg',
  'mov',
  'avi',
  'quicktime',

  'csv',
  'pdf',
  'txt',
  'xlsx',
  'docx',
  'pptx',
  'xls',
  'ppt',
  'doc',
] as const;

export const IMAGE_TYPES = [
  'apng',
  // 'avif',
  'gif',
  'jpg',
  'jpeg',
  'jfif',
  'pjpeg',
  'pjp',
  'png',
  'webp',

  'image/apng',
  // 'image/avif',
  'image/gif',
  'image/jpg',
  'image/jpeg',
  'image/jfif',
  'image/pjpeg',
  'image/pjp',
  'image/png',
  'image/webp',
];

export const AUDIO_TYPES = [
  'mp3',
  'wav',
  '3gp',
  'mpg',
  'mpeg',
  'm4a',
  'oga',
  'ogg',
  'webm',

  'audio/mp3',
  'audio/wav',
  'audio/3gp',
  'audio/mpg',
  'audio/mpeg',
  'audio/m4a',
  'audio/oga',
  'audio/ogg',
  'audio/webm',
];

export const VIDEO_TYPES = [
  'mp4',
  'webm',
  '3gp',
  'mpg',
  'mpeg',
  'm4v',
  'm4p',
  'ogv',
  'ogg',
  'mov',
  'avi',
  'quicktime',

  'video/mp4',
  'video/webm',
  'video/3gp',
  'video/mpg',
  'video/mpeg',
  'video/m4v',
  'video/m4p',
  'video/ogv',
  'video/ogg',
  'video/mov',
  'video/avi',
  'video/quicktime',
  'video/x-matroska',
];

export const DOCUMENT_MIME_WITH_EXTENSIONS_TYPES: Record<string, string[]> = {
  'text/csv': ['.csv'],
  'application/pdf': ['.pdf'],
  'text/plain': ['.txt'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.ms-powerpoint': ['.ppt'],
  'application/msword': ['.doc'],
};
export const DOCUMENT_EXTENSION_TYPES = flatten(
  Object.keys(DOCUMENT_MIME_WITH_EXTENSIONS_TYPES).map((key) =>
    DOCUMENT_MIME_WITH_EXTENSIONS_TYPES[key].map((extension) =>
      extension.slice(extension.indexOf('.') + 1),
    ),
  ),
);
export const DOCUMENT_MIME_TYPES = Object.keys(DOCUMENT_MIME_WITH_EXTENSIONS_TYPES);
export const DOCUMENT_TYPES = [...DOCUMENT_EXTENSION_TYPES, ...DOCUMENT_MIME_TYPES];

export const ATTACHMENT_TYPES = [...IMAGE_TYPES, ...AUDIO_TYPES, ...VIDEO_TYPES, ...DOCUMENT_TYPES];

export const MEDIA_ATTACHMENT_TYPES = [...IMAGE_TYPES, ...AUDIO_TYPES, ...VIDEO_TYPES];

export const AWS_BEDROCK_CHAT_ATTACHMENT_TYPES = {
  DOCUMENT_TYPES: [
    'pdf',
    'csv',
    'doc',
    'docx',
    'xls',
    'xlsx',
    'html',
    'txt',
    'md',
    'text/csv',
    'application/pdf',
    'text/plain',
    'text/markdown',
    'text/html',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/msword',
  ],
  IMAGE_TYPES: ['png', 'jpeg', 'gif', 'webp', 'image/gif', 'image/png', 'image/jpeg', 'image/webp'],
  VIDEO_TYPES: [
    'mkv',
    'mov',
    'mp4',
    'webm',
    'flv',
    'mpeg',
    'mpg',
    'wmv',
    '3gp',
    'video/x-matroska',
    'video/webm',
    'video/quicktime',
    'video/mp4',
    'video/x-flv',
    'video/mpeg',
    'video/x-ms-wmv',
    'video/3gpp',
  ],
};

export const GCP_VERTEX_AI_ATTACHMENT_TYPES = [
  // Image types
  'png',
  'jpeg',
  'webp',
  'image/png',
  'image/jpeg',
  'image/webp',

  // Doc types
  'pdf',
  'txt',
  'application/pdf',
  'text/plain',

  // Video types
  'flv',
  'mov',
  'mpeg',
  'mpg',
  'mp4',
  'webm',
  'wmv',
  '3gp',
  'video/x-flv',
  'video/quicktime',
  'video/mpeg',
  'video/mpegs',
  'video/mpg',
  'video/mp4',
  'video/webm',
  'video/wmv',
  'video/3gpp',

  // Audio types
  'audio/x-aac',
  'audio/flac',
  'audio/mp3',
  'audio/m4a',
  'audio/mpeg',
  'audio/mpga',
  'audio/mp4',
  'audio/opus',
  'audio/pcm',
  'audio/wav',
  'audio/webm',
];

export const DEFAULT_GPT_CHAT_SERVICE_PROVIDER = 'aws';
export const MAX_FILE_SIZE_MB = 5;
export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
